import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const LandingUpcomingEvents = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/events`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const nextMonth = new Date(today);
      nextMonth.setDate(today.getDate() + 30);
      
      const filtered = res.data
        .filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= today && eventDate <= nextMonth;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setUpcomingEvents(filtered);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="bg-[#f5f3f0] py-12 sm:py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className='flex flex-col items-center mb-4 sm:mb-6 md:mb-8'>
            <p className='font-black text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 uppercase tracking-wider'>
              Next 30 Days
            </p>
            <h2 className='font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 mt-2 md:mt-3 leading-tight text-center uppercase'>
              Upcoming Events
            </h2>
            <img className="object-cover my-3 sm:my-4 md:my-6 w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px]" src="images/hrlineimg.png" alt="hrlineimg" />
          </div>
        </motion.div>

        {/* Events Grid */}
        {upcomingEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={event._id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
                  
                  {/* Top Image Section - Full Width */}
                  <div className="relative h-64 sm:h-60 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                    {/* Date Badge */}
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-xl">
                      <p className="text-3xl sm:text-4xl font-black text-gray-900 leading-none text-center">
                        {new Date(event.date).getDate()}
                      </p>
                      <p className="text-[10px] font-black uppercase text-gray-600 text-center mt-1">
                        {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                      </p>
                    </div>

                   
                  </div>

                  {/* Bottom Content Section */}
                  <div className="p-6 sm:p-8 bg-white flex flex-col flex-grow">
                    
                    {/* Title */}
                    <h3 className="text-xl sm:text-2xl font-black text-gray-900 uppercase leading-tight line-clamp-2 mb-4">
                      {event.title}
                    </h3>

                    {/* Details with Icons */}
                    <div className="space-y-3 mb-6 flex-grow">
                      
                      {/* Date */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10  flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-500 uppercase tracking-wider">Date</p>
                          <p className="text-sm font-light text-gray-900">
                            {new Date(event.date).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10  flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-500 uppercase tracking-wider">Location</p>
                          <p className="text-sm font-light text-gray-900 line-clamp-2">
                            {event.address}
                          </p>
                        </div>
                      </div>

                      {/* Contact Person */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10  flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-500 uppercase tracking-wider">Contact</p>
                          <p className="text-sm font-light text-gray-900">
                            {event.pointOfContact}
                          </p>
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10  flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-500 uppercase tracking-wider">Phone</p>
                          <a 
                            href={`tel:${event.number}`}
                            className="text-sm font-light text-gray-900 hover:text-gray-700 transition-colors"
                          >
                            {event.number}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <Link to='/eventslist'>
                      <button className="w-full group/btn flex items-center justify-center bg-gray-900 gap-2 px-4 py-3 rounded-xl text-white font-black text-sm uppercase hover:bg-gray-800 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-xl">
                        <span>Learn More</span>
                        <svg className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center py-16"
          >
            <div className="inline-block p-12 bg-white rounded-3xl shadow-lg">
              <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-black text-gray-700 mb-3 uppercase">
                No Events Scheduled
              </h3>
              <p className="text-gray-500 text-base font-light">
                Check back soon for upcoming events
              </p>
            </div>
          </motion.div>
        )}

        {/* View All Button */}
        {upcomingEvents.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center mt-12 sm:mt-16"
          >
            <Link to='/eventslist'>
              <button className="inline-flex items-center gap-3 px-8 py-4 bg-gray-900 text-white text-sm font-black uppercase rounded-2xl hover:bg-gray-800 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group">
                <span>View All Events</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LandingUpcomingEvents;




// old working code 16/12/25


// import React, { useState, useEffect } from 'react';
// import axios from 'axios';

// const LandingUpcomingEvents = () => {
//   const [upcomingEvents, setUpcomingEvents] = useState([]);

//   useEffect(() => {
//     fetchUpcomingEvents();
//   }, []);

//   const fetchUpcomingEvents = async () => {
//   try {
//     const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/events`);
//     const today = new Date();
//     today.setHours(0, 0, 0, 0); // Normalize to avoid time issues

//     const filtered = res.data
//       .filter(event => new Date(event.date) >= today)
//       .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by soonest first

//     setUpcomingEvents(filtered);
//   } catch (err) {
//     console.error(err);
//   }
// };


//   return (
//     <div className="">

//       <div className="max-w-3xl mx-auto p-6">
//         <div className="flex flex-col items-center mt-10 lg:mb-0 ">
//           <p className="font-black text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 uppercase tracking-wider">Event & Blog</p>
//           <p className="font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl  text-center">Our Upcoming Blogs & Events</p>
//         </div>

//         <div className="flex flex-col md:flex gap-10">
//           {upcomingEvents.map(event => (
//             <div
//               key={event._id}
//               className="flex flex-col md:flex-row 
//                 bg-white/20 backdrop-blur-md 
//                 border border-white/30 
//                 rounded-2xl overflow-hidden 
//                 shadow-lg hover:scale-[1.02] 
//                 transition-transform duration-300"
//             >
//               <div className="md:w-1/2 h-64">
//                 <img
//                   src={event.image}
//                   alt={event.title}
//                   className="w-full h-full object-cover"
//                 />
//               </div>

//               <div className="md:w-1/2 p-6 flex flex-col justify-center text-gray-800">
//                 <h2 className="text-2xl font-bold mb-3">{event.title}</h2>
//                 <p className="text-gray-700 mb-2">
//                   <strong>Date:</strong> {new Date(event.date).toLocaleDateString()}
//                 </p>
//                 <p className="text-gray-700 mb-2">
//                   <strong>Contact:</strong> {event.pointOfContact} ({event.number})
//                 </p>
//                 <p className="text-gray-700">
//                   <strong>Address:</strong> {event.address}
//                 </p>
//               </div>
//             </div>
//           ))}
//         </div>

//       </div>
//     </div>
//   );
// };

// export default LandingUpcomingEvents;



